<template>
  <HrPageLayout
    title="Staff Establishment"
    subtitle="County Public Service Board · Approved structures, positions, documents, and audit trail"
    icon="🏛️"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Public Service Board' },
      { label: 'Staff Establishment' }
    ]"
  >
    <template #header-actions>
      <router-link class="hr-btn hr-btn-outline-primary" to="/position-management">Designation management</router-link>
      <router-link class="hr-btn hr-btn-outline-primary" to="/org-structure">Org units</router-link>
      <router-link class="hr-btn hr-btn-outline-primary" to="/employee-register">Employee register</router-link>
      <button type="button" class="hr-btn hr-btn-primary" :disabled="loading" @click="reloadAll">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh data
      </button>
    </template>

    <div class="tabs-row">
      <button v-for="t in tabDefs" :key="t.id" type="button" :class="['tab', tab === t.id ? 'on' : '']" @click="tab = t.id">{{ t.label }}</button>
    </div>

    <!-- Org chart -->
    <div v-show="tab === 'org'" class="panel">
      <div class="breadcrumb">
        <button type="button" class="bc" @click="chartReset">County</button>
        <template v-if="chartDept">
          <span class="sep">›</span>
          <button type="button" class="bc" @click="chartDeptOnly">{{ chartDept.name }}</button>
        </template>
        <template v-if="chartUnit">
          <span class="sep">›</span>
          <span class="bc muted">{{ chartUnit.name }}</span>
        </template>
      </div>
      <p class="muted">{{ countyName }} — navigate Department → Unit → Position (live from register & positions).</p>

      <div v-if="!chartDept" class="card-grid">
        <button v-for="d in departments" :key="'d-' + d.id" type="button" class="tile" @click="openDept(d)">
          <strong>{{ d.name }}</strong>
          <span class="small">{{ d.units?.length || 0 }} units · {{ countPos(d) }} positions</span>
        </button>
      </div>

      <div v-else-if="!chartUnit" class="card-grid">
        <button v-for="u in chartDept.units" :key="'u-' + u.id" type="button" class="tile" @click="openUnit(u)">
          <strong>{{ u.name }}</strong>
          <span class="small">{{ u.positions?.length || 0 }} positions</span>
        </button>
      </div>

      <div v-else class="table-wrap">
        <p class="muted small hr-mb-2">
          <strong>Proposed</strong> = establishment cap (designation management).
          <strong>In post</strong> = active staff (employee register).
          <strong>Difference</strong> = proposed − in post.
        </p>
        <table class="hr-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Position</th>
              <th class="num">Proposed</th>
              <th class="num">In post</th>
              <th class="num">Difference</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in chartUnit.positions" :key="'p-' + p.id">
              <td>{{ p.code }}</td>
              <td>{{ p.name }}</td>
              <td class="num">{{ formatProposed(p) }}</td>
              <td class="num">{{ p.in_post != null ? p.in_post : p.filled }}</td>
              <td class="num" :class="differenceClass(p)">{{ formatDifference(p) }}</td>
              <td>
                <span v-if="p.is_abolished" class="pill bad">Abolished</span>
                <span v-else-if="p.is_new" class="pill new">New</span>
                <span v-else-if="p.over_staffed > 0" class="pill bad">Over by {{ p.over_staffed }}</span>
                <span v-else-if="p.vacant_slots > 0" class="pill warn">{{ p.vacant_slots }} vacant</span>
                <span v-else class="pill ok">At establishment</span>
              </td>
            </tr>
            <tr v-if="chartUnit.positions?.length" class="totals-row">
              <td colspan="2"><strong>Unit totals</strong></td>
              <td class="num"><strong>{{ unitTotals(chartUnit).proposed }}</strong></td>
              <td class="num"><strong>{{ unitTotals(chartUnit).inPost }}</strong></td>
              <td class="num"><strong :class="differenceClass({ difference: unitTotals(chartUnit).difference })">{{ unitTotals(chartUnit).differenceLabel }}</strong></td>
              <td></td>
            </tr>
            <tr v-if="!chartUnit.positions?.length">
              <td colspan="6" class="muted center">No positions in this unit.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Documents -->
    <div v-show="tab === 'docs'" class="panel">
      <div class="split">
        <div>
          <h3 class="h3">Upload establishment document</h3>
          <label class="lbl">Title</label>
          <input v-model="docForm.title" type="text" class="inp" />
          <label class="lbl">Description</label>
          <textarea v-model="docForm.description" class="inp ta" rows="2"></textarea>
          <label class="lbl">File</label>
          <input ref="docFile" type="file" class="inp" />
          <button type="button" class="hr-btn hr-btn-primary hr-mt-2" :disabled="docSaving" @click="uploadDoc">{{ docSaving ? "Uploading…" : "Upload" }}</button>
        </div>
        <div>
          <h3 class="h3">Library</h3>
          <div class="table-wrap">
            <table class="hr-table">
              <thead>
                <tr><th>Title</th><th>When</th><th class="doc-actions-th">Actions</th></tr>
              </thead>
              <tbody>
                <tr v-for="r in documents" :key="r.id" class="doc-row">
                  <td>
                    <button
                      v-if="r.id"
                      type="button"
                      class="se-doc-title-link"
                      :title="'View: ' + (r.title || 'document')"
                      @click="viewDoc(r)"
                    >
                      <i class="bx bx-file-blank"></i>
                      {{ r.title || 'Untitled' }}
                    </button>
                    <span v-else>{{ r.title }}</span>
                  </td>
                  <td class="doc-date">{{ formatDocDate(r.created_at) }}</td>
                  <td class="doc-actions">
                    <button
                      type="button"
                      class="se-doc-view-btn"
                      title="Open document in a new tab"
                      @click.stop="viewDoc(r)"
                    >
                      <i class="bx bx-show"></i>
                      <span>View</span>
                    </button>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" title="Download file" @click.stop="downloadDoc(r.id)">
                      <i class="bx bx-download"></i>
                    </button>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" title="Remove document" @click.stop="deleteDoc(r.id)">
                      <i class="bx bx-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="!documents.length"><td colspan="3" class="muted center">No documents yet.</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Change requests -->
    <div v-show="tab === 'changes'" class="panel">
      <div class="hr-mb-3">
        <h3 class="h3">Submit establishment change (for approval)</h3>
        <div class="grid-form">
          <input v-model="chgForm.title" type="text" class="inp" placeholder="Title *" />
          <select v-model="chgForm.change_type" class="inp">
            <option value="structure">Structure</option>
            <option value="position_new">New position</option>
            <option value="position_update">Position update</option>
            <option value="position_abolish">Abolish position</option>
            <option value="document_policy">Document / policy</option>
            <option value="other">Other</option>
          </select>
          <textarea v-model="chgForm.details" class="inp ta span2" rows="2" placeholder="Details *"></textarea>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="chgSaving" @click="submitChange">{{ chgSaving ? "Submitting…" : "Submit" }}</button>
        </div>
      </div>
      <div class="table-wrap">
        <table class="hr-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Title</th>
              <th>Type</th>
              <th>Status</th>
              <th>Requested by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in changes" :key="r.id">
              <td>{{ r.created_at }}</td>
              <td>{{ r.title }}</td>
              <td>{{ r.change_type }}</td>
              <td><span class="pill" :class="chgClass(r.status)">{{ r.status }}</span></td>
              <td>{{ r.requested_by }}</td>
              <td>
                <template v-if="r.status === 'pending'">
                  <input v-model="chgRemarks[r.id]" type="text" class="inp sm" placeholder="Remarks" />
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="decide(r.id, 'approved')">Approve</button>
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="decide(r.id, 'rejected')">Reject</button>
                </template>
              </td>
            </tr>
            <tr v-if="!changes.length"><td colspan="6" class="muted center">No change requests.</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Position snapshot -->
    <div v-show="tab === 'snapshot'" class="panel">
      <div class="filters-inline">
        <label>Filter <select v-model="snapFilter" class="inp sm"><option value="">All</option><option value="filled">At establishment</option><option value="vacant">Vacant</option><option value="partial">Partially filled</option><option value="over">Over establishment</option><option value="new">New</option><option value="abolished">Abolished</option></select></label>
      </div>
      <div class="table-wrap">
        <table class="hr-table">
          <thead>
            <tr>
              <th>Department</th>
              <th>Unit</th>
              <th>Position</th>
              <th class="num">Proposed</th>
              <th class="num">In post</th>
              <th class="num">Difference</th>
              <th>Label</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in filteredSnapshot" :key="'s-' + p.id">
              <td>{{ p.departmentName || "—" }}</td>
              <td>{{ p.unit_name || "—" }}</td>
              <td>{{ p.positionName }}</td>
              <td class="num">{{ formatProposed(p) }}</td>
              <td class="num">{{ p.in_post != null ? p.in_post : p.filled_count }}</td>
              <td class="num" :class="differenceClass(p)">{{ formatDifference(p) }}</td>
              <td>{{ formatSlotLabel(p.slot_label) }}</td>
            </tr>
            <tr v-if="!filteredSnapshot.length"><td colspan="7" class="muted center">No data.</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Audit -->
    <div v-show="tab === 'audit'" class="panel">
      <div class="table-wrap">
        <table class="hr-table">
          <thead>
            <tr><th>When</th><th>Action</th><th>Entity</th><th>User</th><th>Details</th></tr>
          </thead>
          <tbody>
            <tr v-for="a in auditRows" :key="a.id">
              <td>{{ a.created_at }}</td>
              <td>{{ a.action }}</td>
              <td>{{ a.entity_type }} #{{ a.entity_id }}</td>
              <td>{{ a.username }}</td>
              <td>{{ a.details }}</td>
            </tr>
            <tr v-if="!auditRows.length"><td colspan="5" class="muted center">No audit entries yet.</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, executeDownload } from "@/api";

export default {
  name: "StaffEstablishmentHub",
  components: { HrPageLayout },
  data() {
    return {
      tab: "org",
      tabDefs: [
        { id: "org", label: "Org chart" },
        { id: "docs", label: "Documents" },
        { id: "changes", label: "Approvals" },
        { id: "snapshot", label: "Positions" },
        { id: "audit", label: "Audit log" }
      ],
      loading: false,
      countyName: "",
      departments: [],
      chartDept: null,
      chartUnit: null,
      documents: [],
      docForm: { title: "", description: "" },
      docSaving: false,
      changes: [],
      chgForm: { title: "", change_type: "structure", details: "" },
      chgSaving: false,
      chgRemarks: {},
      snapshot: [],
      snapFilter: "",
      auditRows: []
    };
  },
  computed: {
    filteredSnapshot() {
      const f = this.snapFilter;
      if (!f) return this.snapshot;
      return this.snapshot.filter((p) => (p.slot_label || "") === f);
    }
  },
  mounted() {
    this.reloadAll();
  },
  methods: {
    chgClass(s) {
      if (s === "approved") return "ok";
      if (s === "rejected") return "bad";
      return "pend";
    },
    countPos(d) {
      let n = 0;
      (d.units || []).forEach((u) => {
        n += (u.positions || []).length;
      });
      return n;
    },
    formatProposed(p) {
      const v = p.proposed != null ? p.proposed : (p.max_employees > 0 ? p.max_employees : null);
      return v != null && v > 0 ? v : "—";
    },
    formatDifference(p) {
      if (p.difference != null) return p.difference;
      const proposed = p.proposed != null ? p.proposed : (p.max_employees > 0 ? Number(p.max_employees) : null);
      const inPost = p.in_post != null ? p.in_post : (p.filled != null ? p.filled : p.filled_count);
      if (proposed == null || proposed <= 0) return "—";
      return proposed - Number(inPost || 0);
    },
    differenceClass(p) {
      let d = p.difference;
      if (d == null) {
        const proposed = p.proposed != null ? p.proposed : (p.max_employees > 0 ? Number(p.max_employees) : null);
        const inPost = p.in_post != null ? p.in_post : (p.filled != null ? p.filled : p.filled_count);
        if (proposed == null || proposed <= 0) return "";
        d = proposed - Number(inPost || 0);
      }
      if (Number(d) < 0) return "diff-over";
      if (Number(d) > 0) return "diff-vacant";
      return "diff-ok";
    },
    formatSlotLabel(label) {
      const map = {
        filled: "At establishment",
        vacant: "Vacant",
        partial: "Partially filled",
        over: "Over establishment",
        new: "New position",
        abolished: "Abolished"
      };
      return map[label] || label || "—";
    },
    unitTotals(unit) {
      const positions = unit?.positions || [];
      let proposed = 0;
      let hasProposed = false;
      let inPost = 0;
      positions.forEach((p) => {
        const prop = p.proposed != null ? p.proposed : (p.max_employees > 0 ? Number(p.max_employees) : 0);
        if (prop > 0) {
          hasProposed = true;
          proposed += prop;
        }
        inPost += Number(p.in_post != null ? p.in_post : p.filled || 0);
      });
      const difference = hasProposed ? proposed - inPost : null;
      return {
        proposed: hasProposed ? proposed : "—",
        inPost,
        difference,
        differenceLabel: difference != null ? difference : "—"
      };
    },
    chartReset() {
      this.chartDept = null;
      this.chartUnit = null;
    },
    chartDeptOnly() {
      this.chartUnit = null;
    },
    openDept(d) {
      this.chartDept = d;
      this.chartUnit = null;
    },
    openUnit(u) {
      this.chartUnit = u;
    },
    async reloadAll() {
      this.loading = true;
      try {
        await Promise.all([this.loadChart(), this.loadDocs(), this.loadChanges(), this.loadSnapshot(), this.loadAudit()]);
      } finally {
        this.loading = false;
      }
    },
    async loadChart() {
      const fd = new FormData();
      fd.append("function", "getStaffEstablishmentOrgChart");
      const res = await execute(fd, authUrl);
      const d = res.data?.data;
      this.countyName = d?.countyName || "County";
      this.departments = Array.isArray(d?.departments) ? d.departments : [];
      this.chartReset();
    },
    async loadDocs() {
      const fd = new FormData();
      fd.append("function", "listStaffEstablishmentDocuments");
      const res = await execute(fd, authUrl);
      this.documents = res.data?.data?.rows || [];
    },
    async loadChanges() {
      const fd = new FormData();
      fd.append("function", "listStaffEstablishmentChanges");
      const res = await execute(fd, authUrl);
      this.changes = res.data?.data?.rows || [];
    },
    async loadSnapshot() {
      const fd = new FormData();
      fd.append("function", "getStaffEstablishmentPositionSnapshot");
      const res = await execute(fd, authUrl);
      this.snapshot = res.data?.data?.positions || [];
    },
    async loadAudit() {
      const fd = new FormData();
      fd.append("function", "listStaffEstablishmentAuditLog");
      const res = await execute(fd, authUrl);
      this.auditRows = res.data?.data?.rows || [];
    },
    async uploadDoc() {
      if (!this.docForm.title.trim()) {
        alert("Title required");
        return;
      }
      const f = this.$refs.docFile?.files?.[0];
      if (!f) {
        alert("Choose a file");
        return;
      }
      this.docSaving = true;
      try {
        const fd = new FormData();
        fd.append("function", "uploadStaffEstablishmentDocument");
        fd.append("title", this.docForm.title.trim());
        fd.append("description", this.docForm.description || "");
        fd.append("file", f);
        const res = await execute(fd, authUrl);
        if (!res.data?.success) {
          alert(res.data?.message || "Upload failed");
          return;
        }
        this.docForm = { title: "", description: "" };
        this.$refs.docFile.value = "";
        await this.loadDocs();
        await this.loadAudit();
      } catch (e) {
        alert(e?.message || "Error");
      } finally {
        this.docSaving = false;
      }
    },
    async fetchDocBlob(id, fnName) {
      const fd = new FormData();
      fd.append("function", fnName);
      fd.append("id", String(id));
      const res = await executeDownload(fd, authUrl);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      if (blob.type && blob.type.indexOf("json") !== -1) {
        const text = await blob.text();
        try {
          const j = JSON.parse(text);
          throw new Error(j.message || "Could not open document.");
        } catch (e) {
          if (e instanceof Error && e.message) throw e;
          throw new Error("Could not open document.");
        }
      }
      return blob;
    },
    suggestedDocName(row) {
      const orig = row?.original_file_name || row?.title || "document";
      return String(orig).replace(/[^A-Za-z0-9._ -]/g, "_");
    },
    formatDocDate(d) {
      if (!d) return "—";
      return String(d).slice(0, 16).replace("T", " ");
    },
    async viewDoc(row) {
      if (!row?.id) return;
      try {
        const blob = await this.fetchDocBlob(row.id, "viewStaffEstablishmentDocument");
        const url = window.URL.createObjectURL(blob);
        const opened = window.open(url, "_blank");
        if (!opened) {
          alert("Pop-up blocked. Allow pop-ups to view the document, or use Download.");
          window.URL.revokeObjectURL(url);
          return;
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 120000);
      } catch (e) {
        alert(e?.message || "Could not view document");
      }
    },
    async downloadDoc(id) {
      try {
        const row = this.documents.find(d => String(d.id) === String(id));
        const blob = await this.fetchDocBlob(id, "downloadStaffEstablishmentDocument");
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = row ? this.suggestedDocName(row) : "document";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      } catch (e) {
        alert(e?.message || "Download failed");
      }
    },
    async deleteDoc(id) {
      if (!confirm("Delete this document?")) return;
      const fd = new FormData();
      fd.append("function", "deleteStaffEstablishmentDocument");
      fd.append("id", String(id));
      const res = await execute(fd, authUrl);
      if (res.data?.success) {
        await this.loadDocs();
        await this.loadAudit();
      } else alert(res.data?.message || "Failed");
    },
    async submitChange() {
      if (!this.chgForm.title.trim() || !this.chgForm.details.trim()) {
        alert("Title and details required");
        return;
      }
      this.chgSaving = true;
      try {
        const fd = new FormData();
        fd.append("function", "submitStaffEstablishmentChange");
        fd.append("title", this.chgForm.title.trim());
        fd.append("change_type", this.chgForm.change_type);
        fd.append("details", this.chgForm.details.trim());
        const res = await execute(fd, authUrl);
        if (!res.data?.success) {
          alert(res.data?.message || "Failed");
          return;
        }
        this.chgForm = { title: "", change_type: "structure", details: "" };
        await this.loadChanges();
        await this.loadAudit();
      } catch (e) {
        alert(e?.message || "Error");
      } finally {
        this.chgSaving = false;
      }
    },
    async decide(id, decision) {
      const fd = new FormData();
      fd.append("function", "decideStaffEstablishmentChange");
      fd.append("id", String(id));
      fd.append("decision", decision);
      fd.append("remarks", this.chgRemarks[id] || "");
      const res = await execute(fd, authUrl);
      if (res.data?.success) {
        this.chgRemarks[id] = "";
        await this.loadChanges();
        await this.loadAudit();
      } else alert(res.data?.message || "Failed");
    }
  }
};
</script>

<style scoped>
.tabs-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.tab {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  color: #475569;
}
.tab.on {
  background: #0f172a;
  color: #fff;
  border-color: #0f172a;
}
.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 16px;
}
.muted {
  color: #64748b;
  font-size: 13px;
}
.breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.bc {
  background: none;
  border: none;
  color: #059669;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.bc.muted {
  color: #334155;
  cursor: default;
}
.sep {
  color: #94a3b8;
}
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}
.tile {
  text-align: left;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
}
.tile:hover {
  border-color: #059669;
}
.tile .small {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-top: 6px;
}
.table-wrap {
  overflow: auto;
}
.h3 {
  margin: 0 0 10px 0;
  font-size: 1rem;
}
.split {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 24px;
}
@media (max-width: 900px) {
  .split {
    grid-template-columns: 1fr;
  }
}
.lbl {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin: 8px 0 4px;
}
.inp {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}
.inp.sm {
  max-width: 160px;
  display: inline-block;
  margin-right: 6px;
}
.ta {
  min-height: 64px;
}
.hr-mt-2 {
  margin-top: 8px;
}
.grid-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  max-width: 720px;
}
.span2 {
  grid-column: span 2;
}
.pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}
.pill.ok {
  background: #dcfce7;
  color: #166534;
}
.pill.bad {
  background: #fee2e2;
  color: #991b1b;
}
.pill.pend {
  background: #e0f2fe;
  color: #075985;
}
.pill.new {
  background: #fef3c7;
  color: #92400e;
}
.pill.warn {
  background: #ffedd5;
  color: #9a3412;
}
.center {
  text-align: center;
}
.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.diff-vacant {
  color: #b45309;
  font-weight: 600;
}
.diff-over {
  color: #b91c1c;
  font-weight: 600;
}
.diff-ok {
  color: #047857;
  font-weight: 600;
}
.totals-row {
  background: #f8fafc;
  border-top: 2px solid #e2e8f0;
}
.hr-mb-2 {
  margin-bottom: 8px;
}
.doc-actions-th {
  min-width: 200px;
  text-align: right;
}

.doc-date {
  white-space: nowrap;
  color: #64748b;
  font-size: 13px;
}

.doc-row:hover {
  background: #f0fdf4;
}

.se-doc-title-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: none;
  color: #047857;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  max-width: 100%;
}

.se-doc-title-link:hover {
  color: #059669;
  text-decoration: underline;
}

.se-doc-title-link i {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.doc-actions {
  white-space: nowrap;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}

.se-doc-view-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  min-width: 88px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.se-doc-view-btn i {
  font-size: 1.15rem;
}

.se-doc-view-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #065f46 100%);
  box-shadow: 0 4px 14px rgba(5, 150, 105, 0.45);
  transform: translateY(-1px);
}

.se-doc-view-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(5, 150, 105, 0.3);
}

.se-doc-view-btn:focus-visible {
  outline: 3px solid #86efac;
  outline-offset: 2px;
}
.filters-inline {
  margin-bottom: 12px;
}
</style>
